// docs/116 CI2/CI8/CI15/CI16: the inn's own furniture, painted pixel by pixel like the rest of the room
// (VillageView's `paintRoom`): 24 px cells, `INK` outlines, a warm wood and the Oryx world atlas where it
// has a piece. Two kinds of painting:
//   • `paintInnBack` — one canvas the size of the room for what stands against the back wall or hangs in
//     the rafters (the gallery and its doors, the stairs, the noticeboard, the kitchen's stable door and
//     its bell bracket, the bard's corner, the log basket and fire irons, the season's dressing, frost on
//     the glass); it lies over the room's own canvas and under every figure.
//   • the pieces (`PIECES`) — each a small canvas with its own depth on the stage, so a sitter sits *in*
//     an armchair (its back behind them, its arm and seat over their legs), a sleeper lies under a quilt
//     and the dice table's top hides the knees of those behind it.
// Everything is tinted with the room's light (day or night) the way the room is; what glows (lanterns,
// the kitchen's warm hatch, paper lanterns) is drawn after the tint.
import { frame as worldFrame } from '../../../../game/tiling/oryx';
import { CELL, hashN, type InnPlan, type Room } from '../layout';
import type { Atlas } from '../sprites';
import type { SeasonKey } from '../../../../core/story/calendar';

export type Ctx = CanvasRenderingContext2D;
export const INK = '#1b1514';
const TIMBER = '#5a3922', TIMBER_HI = '#7b5233';
/** A warm oak for the inn's own furniture. */
export const OAK = { d: '#4a2a16', m: '#6e4426', l: '#8e5a32', h: '#b07846' };
const CLOTH = {
  red: { d: '#5e1a18', m: '#8a2a24', l: '#ad3e30', h: '#cf6046' },
  green: { d: '#1f3a26', m: '#2f5a38', l: '#437a4a', h: '#62995c' },
};
const BRASS = { d: '#8a5a1a', m: '#c8902e', h: '#f0cc6a' };

export function rect(c: Ctx, x: number, y: number, w: number, h: number, col: string): void {
  if (w <= 0 || h <= 0) return;
  c.fillStyle = col; c.fillRect(Math.round(x), Math.round(y), Math.round(w), Math.round(h));
}
const px = (c: Ctx, x: number, y: number, col: string) => rect(c, x, y, 1, 1, col);
export function shade(hex: string, f: number): string {
  const n = parseInt(hex.slice(1), 16);
  const ch = (v: number) => Math.max(0, Math.min(255, Math.round(f >= 0 ? v + (255 - v) * f : v * (1 + f))));
  return `#${[(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => ch(v).toString(16).padStart(2, '0')).join('')}`;
}
/** A filled ellipse, row by row (crisp, no anti-aliasing). */
function ellipse(c: Ctx, cx: number, cy: number, rx: number, ry: number, col: string) {
  for (let y = -Math.floor(ry); y <= Math.floor(ry); y++) {
    const half = Math.round(rx * Math.sqrt(Math.max(0, 1 - (y * y) / (ry * ry))));
    rect(c, cx - half, cy + y, half * 2, 1, col);
  }
}
function drawWorld(c: Ctx, a: Atlas | undefined, n: number, x: number, y: number) {
  const f = a?.frames[worldFrame(n)];
  if (!a || !f) return;
  c.drawImage(a.img, f.x, f.y, f.w, f.h, Math.round(x), Math.round(y + CELL - f.h), f.w, f.h);
}

/** The room's light, as VillageView tints the inn (multiplied into everything painted). */
export const innTint = (night: boolean): string => (night ? '#8a6a5c' : '#d8b898');
/** Multiply `col` into what is painted, keeping its alpha. */
export function tintCanvas(c: Ctx, col: string): void {
  const { width: w, height: h } = c.canvas;
  const shape = document.createElement('canvas');
  shape.width = w; shape.height = h;
  shape.getContext('2d')!.drawImage(c.canvas, 0, 0);
  c.globalCompositeOperation = 'multiply'; c.fillStyle = col; c.fillRect(0, 0, w, h);
  c.globalCompositeOperation = 'destination-in'; c.drawImage(shape, 0, 0);
  c.globalCompositeOperation = 'source-over';
}

/** A small lantern (atlas pixels), lit. */
function lantern(c: Ctx, x: number, y: number, lit = true) {
  rect(c, x + 1, y, 4, 1, INK); rect(c, x, y + 1, 6, 1, INK);
  rect(c, x, y + 2, 6, 6, INK);
  rect(c, x + 1, y + 3, 4, 4, lit ? '#ffd46a' : '#4a4f5c');
  if (lit) rect(c, x + 2, y + 4, 2, 2, '#fff3c0');
  rect(c, x + 1, y + 8, 4, 1, INK);
}

// ---- pixel art for the DOM bits (mugs, the lute, the book, the cat asleep …) ---------------------
export interface Pix { url: string; w: number; h: number }
const pixCache = new Map<string, Pix>();
/** Rows of palette letters (`.` clear) painted once into a data URL. */
export function pixArt(key: string, rows: readonly string[], pal: Record<string, string>): Pix {
  const hit = pixCache.get(key);
  if (hit) return hit;
  const h = rows.length, w = Math.max(...rows.map((r) => r.length));
  let url = '';
  if (typeof document !== 'undefined') {
    const cv = document.createElement('canvas');
    cv.width = w; cv.height = h;
    const x = cv.getContext('2d');
    if (x) { rows.forEach((row, j) => { for (let i = 0; i < row.length; i++) { const col = pal[row[i]!]; if (col) { x.fillStyle = col; x.fillRect(i, j, 1, 1); } } }); url = cv.toDataURL(); }
  }
  const v = { url, w, h };
  pixCache.set(key, v);
  return v;
}

// ---- the back: gallery, stairs, the wall's furniture, the season --------------------------------
export interface BackOpts {
  room: Room; plan: InnPlan; night: boolean; season?: SeasonKey; feast: boolean; world?: Atlas; seed: number; upstairs: boolean;
}

/** The inn's back wall and rafters (or the room upstairs): returns the tinted canvas and its glow. */
export function paintInnBack(o: BackOpts): { base: HTMLCanvasElement; glow: HTMLCanvasElement } {
  const { room } = o;
  const W = room.w * CELL, H = room.h * CELL;
  const base = document.createElement('canvas'); base.width = W; base.height = H;
  const glow = document.createElement('canvas'); glow.width = W; glow.height = H;
  const c = base.getContext('2d')!, g = glow.getContext('2d')!;
  c.imageSmoothingEnabled = false; g.imageSmoothingEnabled = false;
  if (o.upstairs) paintUpstairsBack(c, g, o);
  else paintDownstairsBack(c, g, o);
  paintSeason(c, g, o);
  tintCanvas(c, innTint(o.night));
  return { base, glow };
}

function paintDownstairsBack(c: Ctx, g: Ctx, o: BackOpts) {
  const { room, plan } = o;
  const F = room.floor * CELL, T = room.top * CELL, W = room.w * CELL;
  // ── the gallery across the upper back wall, the rooms' doors along it ──
  if (plan.gallery) paintGallery(c, g, o);
  // ── the stairs climbing the right-hand wall ──
  paintStairs(c, o);
  // ── the noticeboard: cork in a frame under a little shingled roof, pinned papers ──
  {
    const n = plan.notice;
    const x = Math.round(n.x * CELL), y = Math.round(n.y * CELL), w = Math.round(n.w * CELL), h = Math.round(n.h * CELL) - 8;
    rect(c, x - 2, y - 1, w + 4, h + 4, INK); rect(c, x - 1, y, w + 2, h + 2, OAK.l); rect(c, x - 1, y, w + 2, 1, OAK.h);
    rect(c, x + 2, y + 3, w - 4, h - 4, '#a8743e');
    for (let i = 0; i < (w * h) / 9; i++) px(c, x + 2 + Math.floor(hashN(o.seed, i, 3) * (w - 4)), y + 3 + Math.floor(hashN(o.seed, i, 4) * (h - 4)), hashN(i, 5) < 0.5 ? '#946232' : '#b88650');
    // the roof
    for (let k = 0; k < 5; k++) rect(c, x - 4 + k, y - 6 + k, w + 8 - k * 2, 1, INK);
    for (let k = 0; k < 4; k++) rect(c, x - 3 + k, y - 5 + k, w + 6 - k * 2, 1, k % 2 ? '#7a3428' : '#94443a');
    rect(c, x + w / 2 - 1, y + h + 3, 2, 3, INK);
    // the notices: a wanted poster, a lost goat, scraps; pins in red and blue
    const papers: Array<[number, number, number, number, string]> = [[3, 4, 11, 14, '#efe4c4'], [16, 3, 12, 10, '#e8d8a8'], [30, 5, 13, 12, '#f2ead6'], [5, 20, 10, 9, '#e4d2a0'], [17, 16, 14, 14, '#efe7d2'], [33, 19, 10, 10, '#e8dcb8']];
    for (const [i, [px0, py0, pw, ph, col]] of papers.entries()) {
      if (px0 + pw > w - 2 || py0 + ph > h - 2) continue;
      const X = x + px0, Y = y + py0;
      rect(c, X + 1, Y + 1, pw, ph, '#00000040');
      rect(c, X, Y, pw, ph, col);
      for (let l = 0; l < ph - 4; l += 2) rect(c, X + 2, Y + 3 + l, Math.max(2, pw - 4 - Math.floor(hashN(i, l) * 4)), 1, '#7a6a52');
      if (i === 0) { rect(c, X + 2, Y + 2, pw - 4, 2, '#7a2a22'); rect(c, X + 3, Y + 5, pw - 6, 5, '#c8b48a'); px(c, X + 4, Y + 6, INK); px(c, X + pw - 5, Y + 6, INK); rect(c, X + 4, Y + 8, pw - 8, 1, INK); }
      if (i === 4) { rect(c, X + 3, Y + 4, 6, 3, '#f8f4ea'); rect(c, X + 3, Y + 7, 1, 2, INK); rect(c, X + 8, Y + 7, 1, 2, INK); rect(c, X + 9, Y + 3, 2, 2, '#f8f4ea'); px(c, X + 10, Y + 2, INK); }
      px(c, X + Math.floor(pw / 2), Y, i % 2 ? '#3a5ab8' : '#c8302a');
    }
  }
  // ── the kitchen's stable door: the bottom half shut, the top open on a warm kitchen, a plate on the ledge ──
  {
    const k = plan.kitchen;
    const dx = Math.round(k.x * CELL), top = F - CELL - 6;
    rect(c, dx - 4, top, CELL + 8, CELL + 7, INK); rect(c, dx - 3, top + 1, CELL + 6, CELL + 6, TIMBER); rect(c, dx - 3, top + 1, CELL + 6, 1, TIMBER_HI);
    // the warm kitchen through the top half (in the glow: it shines at night)
    rect(c, dx, top + 4, CELL, 10, '#3a1c0c');
    rect(g, dx, top + 4, CELL, 10, '#c8742e'); rect(g, dx + 2, top + 6, CELL - 4, 6, '#e89a46');
    rect(g, dx + 4, top + 9, 6, 3, '#3a2a22'); rect(g, dx + 5, top + 7, 4, 2, '#5a4a42');                   // a pot on the range
    rect(g, dx + 14, top + 6, 2, 6, '#7a5a3a'); rect(g, dx + 17, top + 5, 2, 7, '#7a5a3a');                 // ladles hanging
    for (let i = 0; i < 3; i++) px(g, dx + 6 + i, top + 5 - i, '#f4e4c8');                                   // steam
    // the bottom half: planks and a latch
    rect(c, dx, top + 14, CELL, CELL - 7, OAK.m);
    for (let x = dx + 5; x < dx + CELL; x += 6) rect(c, x, top + 14, 1, CELL - 7, OAK.d);
    rect(c, dx, top + 14, CELL, 1, OAK.h); rect(c, dx + 2, top + 20, CELL - 4, 2, OAK.d);
    rect(c, dx + CELL - 5, top + 22, 2, 2, BRASS.m);
    // the ledge and a plate waiting on it
    rect(c, dx - 4, top + 13, CELL + 8, 3, INK); rect(c, dx - 3, top + 13, CELL + 6, 1, OAK.h); rect(c, dx - 3, top + 14, CELL + 6, 1, OAK.l);
    rect(c, dx + 6, top + 11, 9, 2, '#e8e0d0'); rect(c, dx + 7, top + 10, 7, 1, '#f4f0e4');
    // a painted pot-and-ladle sign over the door
    rect(c, dx + 4, top - 9, 16, 8, INK); rect(c, dx + 5, top - 8, 14, 6, OAK.l);
    rect(c, dx + 8, top - 6, 7, 3, '#2a2a30'); rect(c, dx + 9, top - 7, 5, 1, '#3a3a44'); rect(c, dx + 15, top - 7, 3, 1, '#2a2a30');
    // the bell's bracket (the bell itself swings in the DOM)
    const bx = Math.round(plan.bell.x * CELL), by = Math.round(plan.bell.y * CELL);
    rect(c, bx - 1, by - 4, 8, 2, INK); rect(c, bx + 5, by - 6, 2, 6, INK); rect(c, bx - 1, by - 4, 7, 1, '#4a4a52');
  }
  // ── the bard's corner: a lute case against the wall, a woven rug, a hat for coins ──
  {
    const b = plan.bard;
    const lx = Math.round((b.x - 0.55) * CELL), ly = F - 22;
    ellipse(c, lx + 4, ly + 17, 6, 7, INK); rect(c, lx + 2, ly, 5, 12, INK);
    ellipse(c, lx + 4, ly + 17, 5, 6, '#7a4a2a'); rect(c, lx + 3, ly + 1, 3, 11, '#7a4a2a'); rect(c, lx + 3, ly + 1, 1, 11, '#9a6a3a');
    ellipse(c, lx + 3, ly + 15, 2, 3, '#9a6a3a'); rect(c, lx + 2, ly + 11, 5, 1, BRASS.m); rect(c, lx + 1, ly + 20, 7, 1, BRASS.m); px(c, lx + 4, ly + 2, BRASS.h);
    const rx = Math.round((b.x + 0.5) * CELL), ry = F + 46;
    ellipse(c, rx, ry, 22, 5, INK); ellipse(c, rx, ry, 21, 4, '#7a3a5a');
    ellipse(c, rx, ry, 17, 3, '#c8a050'); ellipse(c, rx, ry, 13, 2, '#3a6a7a'); ellipse(c, rx, ry, 8, 1, '#c8a050');
    for (let i = -20; i <= 20; i += 3) px(c, rx + i, ry + 5, '#c8a050');
    // the hat, upturned, a few coins in it
    const hx = Math.round((b.x + 1.15) * CELL), hy = F + 38;
    ellipse(c, hx, hy + 3, 7, 2, INK); ellipse(c, hx, hy + 3, 6, 1.4, '#4a3a5a'); rect(c, hx - 4, hy - 1, 8, 4, INK); rect(c, hx - 3, hy - 1, 6, 3, '#5a4a6a');
    rect(c, hx - 2, hy - 1, 4, 1, '#2a2030'); px(c, hx - 1, hy - 2, BRASS.h); px(c, hx + 1, hy - 1, BRASS.m);
  }
  // ── by the hearth: a basket of logs, the fire irons on their stand ──
  if (room.hearth) {
    const hx = room.hearth.x * CELL - 6, hw = 3 * CELL + 12;
    const bx = hx - 17, by = F - 4;
    rect(c, bx - 1, by - 1, 15, 11, INK); rect(c, bx, by, 13, 9, '#8a6a3a');
    for (let x = bx + 1; x < bx + 13; x += 2) rect(c, x, by, 1, 9, '#6a4a22');
    rect(c, bx, by + 3, 13, 1, '#a88a52');
    for (const [lx, ly] of [[bx + 1, by - 4], [bx + 6, by - 5], [bx + 3, by - 8], [bx + 8, by - 3]] as const) { rect(c, lx - 1, ly - 1, 6, 5, INK); rect(c, lx, ly, 4, 3, '#7a4a28'); rect(c, lx, ly, 1, 3, '#c0925e'); }
    const ix = hx + hw + 2, iy = F - 26;
    rect(c, ix + 2, iy, 1, 26, INK); rect(c, ix + 5, iy + 2, 1, 24, INK); rect(c, ix + 8, iy + 1, 1, 25, INK);
    rect(c, ix + 1, iy - 2, 3, 3, '#4a4a52'); rect(c, ix + 4, iy, 3, 3, '#4a4a52'); rect(c, ix + 7, iy - 1, 3, 3, '#4a4a52');
    rect(c, ix, iy + 24, 11, 3, INK); rect(c, ix + 1, iy + 24, 9, 1, '#5a5a62');
  }
  // ── shadow along the foot of the back wall where the inn's things stand ──
  rect(c, 0, F, W, 1, '#00000020');
  void T;
}

/** CI2: a gallery across the upper back wall: plaster and posts, three doors to the rooms, a railing on
 * the tie beam, lanterns along it (lit), the stairs' landing at its right end. */
/** The gallery's span in atlas px: from clear of the chimney stack to the right-hand wall. */
function gallerySpan(room: Room, plan: InnPlan): { x0: number; x1: number; floor: number } {
  let x0 = Math.round((plan.gallery?.x0 ?? 0) * CELL);
  if (room.hearth) x0 = Math.max(x0, room.hearth.x * CELL - 6 + 3 * CELL + 12 - 10);
  return { x0, x1: room.w * CELL, floor: room.top * CELL - 8 };
}

function paintGallery(c: Ctx, g: Ctx, o: BackOpts) {
  const { room, plan } = o;
  const gal = plan.gallery!;
  const { x0, x1, floor } = gallerySpan(room, plan);    // the gallery's floor is the tie beam's top
  const wallTop = floor - 46;
  // the upper wall, in the rafters' shade
  rect(c, x0, wallTop - 1, x1 - x0, floor - wallTop + 1, INK);
  rect(c, x0 + 1, wallTop, x1 - x0 - 1, floor - wallTop, '#b89a74');
  for (let i = 0; i < ((x1 - x0) * (floor - wallTop)) / 30; i++) px(c, x0 + 1 + Math.floor(hashN(o.seed, i, 21) * (x1 - x0 - 2)), wallTop + Math.floor(hashN(o.seed, i, 22) * (floor - wallTop)), hashN(i, 23) < 0.5 ? '#a88a66' : '#c4a882');
  // shadow under the roof
  for (let k = 0; k < 14; k++) { c.globalAlpha = 0.5 * (1 - k / 14); rect(c, x0 + 1, wallTop + k, x1 - x0 - 1, 1, '#1a0e06'); }
  c.globalAlpha = 1;
  // the roof beam over the gallery and the posts down to the rail
  rect(c, x0, wallTop - 4, x1 - x0, 5, INK); rect(c, x0, wallTop - 3, x1 - x0, 3, TIMBER); rect(c, x0, wallTop - 3, x1 - x0, 1, TIMBER_HI);
  for (let x = x0 + 4; x < x1 - 4; x += CELL * 3) { rect(c, x - 1, wallTop, 6, floor - wallTop, INK); rect(c, x, wallTop, 4, floor - wallTop, TIMBER); rect(c, x, wallTop, 1, floor - wallTop, TIMBER_HI); }
  // the rooms' doors, each with a number plate and a little lamp beside
  for (const [i, d] of gal.doors.entries()) {
    const dx = Math.round(d * CELL);
    if (dx < x0 + 6 || dx + CELL > x1 - 30) continue;
    rect(c, dx - 3, floor - CELL - 5, CELL + 6, CELL + 5, INK); rect(c, dx - 2, floor - CELL - 4, CELL + 4, CELL + 4, TIMBER);
    drawWorld(c, o.world, 207, dx, floor - CELL + 1);
    rect(c, dx + 9, floor - CELL - 3, 6, 3, BRASS.m); px(c, dx + 11, floor - CELL - 2, INK); if (i > 0) px(c, dx + 12, floor - CELL - 2, INK);
    lantern(g, dx + CELL + 4, floor - CELL - 2);
    rect(c, dx + CELL + 3, floor - CELL - 4, 8, 1, INK);
  }
  // the chains of the room's own lights come down in front of the gallery
  for (const hg of room.hangs) { const x = Math.round(hg.x * CELL); if (x > x0 && x < x1) rect(c, x, wallTop - 6, 1, floor - wallTop + 8, '#3a3438'); }
}

/** The gallery's railing (its own depth, so someone up there stands behind it), a quilt airing over it. */
function galleryRail(room: Room, plan: InnPlan): Piece {
  const gal = plan.gallery!;
  const sp = gallerySpan(room, plan);
  const X = sp.x0, Y = sp.floor - 23;
  return { key: 'gallery:rail', x: X, y: Y, w: sp.x1 - sp.x0, h: 24, z: z(gal.y, 5), draw: (c) => {
    const x0 = 0, x1 = sp.x1 - sp.x0, floor = 23;
    const air = Math.round((gal.doors[1] ?? gal.x0 + 6) * CELL) + CELL + 14 - X;
    // the railing: a top rail and balusters standing on the tie beam
  rect(c, x0, floor - 17, x1 - x0, 4, INK); rect(c, x0, floor - 16, x1 - x0, 2, OAK.l); rect(c, x0, floor - 16, x1 - x0, 1, OAK.h);
  for (let x = x0 + 2; x < x1; x += 4) { rect(c, x, floor - 13, 2, 13, INK); px(c, x, floor - 12, OAK.m); rect(c, x, floor - 10, 1, 8, OAK.m); }
  for (let x = x0 + 10; x < x1; x += CELL * 2) { rect(c, x - 1, floor - 21, 5, 21, INK); rect(c, x, floor - 20, 3, 20, OAK.m); rect(c, x, floor - 20, 1, 20, OAK.h); rect(c, x - 1, floor - 22, 5, 2, OAK.d); }
  if (air + 30 < x1 - 40) {
    const qc = ['#b8443a', '#e8c45a', '#4a7aa8', '#efe4c8', '#5a8a4a'];
    rect(c, air - 1, floor - 18, 26, 16, INK);
    for (let j = 0; j < 4; j++) for (let i = 0; i < 6; i++) rect(c, air + i * 4, floor - 17 + j * 4, 4, 4, qc[(i * 3 + j * 2) % qc.length]!);
    for (let i = 0; i < 6; i++) px(c, air + i * 4 + 2, floor - 3, '#2a1a10');
  }
    for (const hg of room.hangs) { const x = Math.round(hg.x * CELL) - X; if (x > 0 && x < x1) rect(c, x, 0, 1, floor, '#3a3438'); }
  } };
}

/** CI16: the stairs climb the right-hand wall from the floor to the gallery (or a hatch in the rafters). */
function paintStairs(c: Ctx, o: BackOpts) {
  const { room, plan } = o;
  const F = room.floor * CELL, T = room.top * CELL, W = room.w * CELL;
  const bx = Math.round((plan.stairs.x - 1) * CELL), foot = F + 6;
  const topY = T - 8, tx = W - 2;
  const rise = foot - topY, run = tx - bx;
  const n = Math.max(6, Math.round(rise / 9));
  const sr = rise / n, sw = run / n;
  // the space under the stairs: dark, a barrel and a crate stored there
  c.fillStyle = '#1a100a';
  c.beginPath(); c.moveTo(bx + sw, foot); c.lineTo(tx, topY + sr); c.lineTo(tx, foot); c.closePath(); c.fill();
  drawWorld(c, o.world, 268, tx - 30, foot - CELL - 2);
  drawWorld(c, o.world, 435, tx - 52, foot - CELL - 2);
  // the steps: tread, riser, nosing
  for (let k = 0; k < n; k++) {
    const x = bx + k * sw, y = foot - (k + 1) * sr;
    rect(c, x - 1, y - 1, sw + 3, sr + 2, INK);
    rect(c, x, y, sw + 1, 2, OAK.h); rect(c, x, y + 2, sw + 1, sr - 2, OAK.m); rect(c, x, y + sr - 1, sw + 1, 1, OAK.d);
  }
  // the stringer under the treads
  for (let k = 0; k <= n; k++) {
    const x = bx + k * sw, y = foot - k * sr;
    rect(c, x, y, sw + 1, 5, INK); rect(c, x, y + 1, sw + 1, 3, TIMBER);
  }
  // the handrail and its balusters on the room side
  const hr = (k: number) => ({ x: bx + k * sw + sw / 2, y: foot - k * sr - 20 });
  for (let k = 1; k < n; k += 1) { const a = hr(k); rect(c, a.x, a.y, 1, 20 - sr / 2, INK); }
  for (let k = 0; k < n; k++) { const a = hr(k), b = hr(k + 1); const steps = Math.ceil(sw); for (let s = 0; s <= steps; s++) { const t = s / steps; rect(c, a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t, 1, 3, s % 2 ? OAK.l : INK); } }
  { const a = hr(0); rect(c, a.x - 2, a.y - 2, 5, foot - a.y + 2, INK); rect(c, a.x - 1, a.y - 1, 3, foot - a.y, OAK.l); rect(c, a.x - 2, a.y - 4, 5, 3, OAK.h); }
  // with no gallery: a landing at the top and a dark hatch into the floor above
  if (!plan.gallery) {
    rect(c, tx - 34, topY - 3, 36, 5, INK); rect(c, tx - 33, topY - 2, 34, 3, OAK.l);
    rect(c, tx - 30, 0, 28, topY - 3, '#0a0604'); rect(c, tx - 31, 0, 1, topY - 3, INK);
  }
}

function paintUpstairsBack(c: Ctx, g: Ctx, o: BackOpts) {
  const { room, plan } = o;
  const F = room.floor * CELL, T = room.top * CELL, W = room.w * CELL;
  // the roof slopes down: a sloping ceiling of boards over the room's rafters
  const cx = W / 2;
  for (let y = 0; y < T - 8; y++) {
    const inset = Math.max(0, Math.round((T - 8 - y) * 1.4 - 20));
    rect(c, 0, y, Math.min(cx, inset), 1, '#140c06'); rect(c, W - Math.min(cx, inset), y, Math.min(cx, inset), 1, '#140c06');
  }
  // a peg rail with a cloak and a hat
  const pr = F - 46, pl = Math.round((plan.beds[0]?.x ?? 2) * CELL) - 4;
  rect(c, pl, pr, 70, 4, INK); rect(c, pl + 1, pr + 1, 68, 2, OAK.l);
  for (let i = 0; i < 4; i++) rect(c, pl + 8 + i * 17, pr + 3, 2, 3, OAK.d);
  rect(c, pl + 5, pr + 5, 9, 18, INK); rect(c, pl + 6, pr + 6, 7, 16, '#3a5a7a'); rect(c, pl + 6, pr + 6, 2, 16, '#4a6a8a');
  ellipse(c, pl + 43, pr + 8, 6, 2, INK); rect(c, pl + 39, pr + 3, 8, 5, INK); rect(c, pl + 40, pr + 4, 6, 4, '#6a4a2a'); rect(c, pl + 37, pr + 8, 12, 1, '#5a3a20');
  // a sampler stitched with a little house, framed
  const sx = Math.round((plan.beds[2]?.x ?? 10) * CELL) + 6, sy = F - 50;
  rect(c, sx - 2, sy - 2, 24, 20, INK); rect(c, sx - 1, sy - 1, 22, 18, OAK.l); rect(c, sx + 1, sy + 1, 18, 14, '#efe6cc');
  rect(c, sx + 6, sy + 7, 8, 6, '#b8443a'); for (let k = 0; k < 4; k++) rect(c, sx + 5 + k, sy + 6 - k, 10 - k * 2, 1, '#5a3a24');
  rect(c, sx + 9, sy + 10, 2, 3, '#3a2418'); for (let i = 0; i < 9; i++) px(c, sx + 2 + i * 2, sy + 14, i % 2 ? '#4a7a4a' : '#c8302a');
  // the stairs down at the left: a banister going down through the floor
  const hx = Math.round(plan.stairs.x * CELL) + 26;
  rect(c, hx, F - 18, 3, 20, INK); rect(c, hx - 2, F - 20, 7, 3, OAK.h);
  void g;
}

// ---- the season and the feasts (CI15) ------------------------------------------------------------
function leaf(c: Ctx, x: number, y: number, col: string, hi: string) { rect(c, x, y, 3, 2, col); px(c, x + 1, y - 1, col); px(c, x, y, hi); }
function berry(c: Ctx, x: number, y: number) { px(c, x, y, '#d8302a'); px(c, x + 1, y + 1, '#a8201a'); }

/** A swag of greenery (or bunting, or a string) hung from x0 to x1, sagging `sag` px from `y`. */
function swag(x0: number, x1: number, y: number, sag: number, each: (x: number, y: number, t: number) => void, step = 3) {
  for (let x = x0; x <= x1; x += step) { const t = (x - x0) / Math.max(1, x1 - x0); each(x, y + Math.sin(t * Math.PI) * sag, t); }
}

function paintSeason(c: Ctx, g: Ctx, o: BackOpts) {
  const { room, plan, season, feast } = o;
  const F = room.floor * CELL, T = room.top * CELL, W = room.w * CELL;
  const posts: number[] = [];
  for (let x = CELL * 2 + 9; x < W; x += CELL * 4) posts.push(x + 3);
  const spans: Array<[number, number]> = [[0, posts[0] ?? W], ...posts.map((p, i) => [p, posts[i + 1] ?? W] as [number, number])];
  const windows = room.windows.map((w) => ({ x: Math.round(w.x * CELL) + 3, y: Math.round(w.y * CELL) + 2, ww: CELL - 6, wh: w.h * CELL - 6 }));
  if (season === 'winter') {
    // holly and ivy along the tie beam
    for (const [a, b] of spans) swag(a + 2, b - 2, T + 1, 6, (x, y, t) => { rect(c, x, y, 3, 1, '#2a4a2a'); if (Math.round(t * 40) % 3 === 0) leaf(c, x, y + 1, '#2f6a3a', '#4f8a4a'); if (Math.round(t * 40) % 7 === 2) berry(c, x + 1, y + 2); });
    // a wreath round the shield over the mantel, a red bow
    if (room.hearth) {
      const wx = (room.hearth.x + 1.17) * CELL + 8, wy = F - 3.6 * CELL + 8;
      for (let a = 0; a < Math.PI * 2; a += 0.22) { const x = wx + Math.cos(a) * 11, y = wy + Math.sin(a) * 11; leaf(c, x - 1, y, hashN(a * 10) < 0.5 ? '#2f6a3a' : '#24502c', '#4f8a4a'); if (hashN(a * 10, 2) < 0.3) berry(c, x, y + 1); }
      rect(c, wx - 4, wy + 9, 3, 3, '#c8302a'); rect(c, wx + 2, wy + 9, 3, 3, '#c8302a'); rect(c, wx - 1, wy + 10, 3, 2, '#a8201a'); rect(c, wx - 2, wy + 12, 2, 4, '#c8302a'); rect(c, wx + 1, wy + 12, 2, 4, '#c8302a');
    }
    // frost in the corners of the glass, a candle on each sill
    for (const w of windows) {
      for (let i = 0; i < 40; i++) {
        const corner = i % 4, r = hashN(i, w.x, 3) * 6;
        const x = corner % 2 ? w.x + w.ww - 1 - Math.floor(hashN(i, 1) * r) : w.x + Math.floor(hashN(i, 1) * r);
        const y = corner < 2 ? w.y + Math.floor(hashN(i, 2) * r) : w.y + w.wh - 1 - Math.floor(hashN(i, 2) * r);
        px(g, x, y, i % 3 ? '#e8f0ff' : '#c8d8f0');
      }
      rect(g, w.x, w.y + w.wh - 2, w.ww, 2, '#dfe8f8');
    }
  } else if (season === 'spring') {
    // a garland of blossom over the bar, flowers on the sills
    const bx0 = plan.bar.x * CELL, bx1 = (plan.bar.x + plan.bar.w) * CELL - 6, by = F - 2 * CELL + 5;
    swag(bx0, bx1, by, 7, (x, y, t) => { rect(c, x, y, 3, 1, '#4a7a3a'); const k = Math.round(t * 30); if (k % 2 === 0) { px(c, x + 1, y - 1, ['#f0a8c8', '#f8f0f0', '#f8d860', '#b8a0e8'][k % 4]!); px(c, x, y + 1, '#f8f0f0'); } }, 2);
    for (const [a, b] of spans) swag(a + 4, b - 4, T + 1, 4, (x, y, t) => { rect(c, x, y, 2, 1, '#5a8a3a'); if (Math.round(t * 30) % 4 === 0) px(c, x, y + 1, '#f8c8d8'); }, 2);
    for (const w of windows) sillPot(c, w, ['#f0a8c8', '#f8d860', '#f8f0f0']);
  } else if (season === 'summer') {
    // green boughs along the beam, a pot of herbs on the sills
    for (const [a, b] of spans) swag(a + 2, b - 2, T + 1, 5, (x, y, t) => { rect(c, x, y, 3, 1, '#3a5a2a'); if (Math.round(t * 40) % 2 === 0) leaf(c, x, y + 1, '#4a8a3a', '#7ab85a'); }, 2);
    for (const w of windows) sillPot(c, w, ['#e8e070', '#ffffff']);
  } else if (season === 'autumn') {
    // strings of dried apple rings along the beam, gourds and a sheaf by the hearth
    for (const [a, b] of spans) swag(a + 4, b - 4, T + 1, 7, (x, y, t) => { px(c, x, y, '#8a6a3a'); if (Math.round(t * 24) % 3 === 0) { rect(c, x - 1, y, 3, 3, '#c88a3a'); px(c, x, y + 1, '#f0d8a0'); } }, 1);
    if (room.hearth) {
      const hx = room.hearth.x * CELL - 6 + 3 * CELL + 12 + 14;
      for (const [k, col] of [[0, '#d8782a'], [8, '#e8a83a'], [4, '#c8602a']] as const) { ellipse(c, hx + k + 3, F + 2 - (k === 4 ? 3 : 0), 4, 3, INK); ellipse(c, hx + k + 3, F + 2 - (k === 4 ? 3 : 0), 3, 2, col); px(c, hx + k + 3, F - 2 - (k === 4 ? 3 : 0), '#4a6a2a'); }
      const sx = room.hearth.x * CELL - 30;
      for (let i = 0; i < 9; i++) { rect(c, sx + i, F - 24 + Math.abs(i - 4), 1, 26 - Math.abs(i - 4), i % 2 ? '#c8a050' : '#e0bc68'); }
      rect(c, sx - 1, F - 10, 11, 2, '#8a5a2a');
    }
    for (const w of windows) { ellipse(c, w.x + w.ww - 4, w.y + w.wh + 1, 3, 2, '#d8782a'); }
  }
  if (feast) {
    // bunting across the room under the beam, and paper lanterns strung over it
    const colours = ['#c8302a', '#e8b83a', '#3a7ab8', '#efe4c8', '#4a8a3a'];
    for (const [i, [a, b]] of spans.entries()) {
      let k = i * 2;
      swag(a + 2, b - 2, T + 3, 9, (x, y) => {
        rect(c, x, y, 6, 1, '#efe4c8');
        const col = colours[k++ % colours.length]!;
        for (let r = 0; r < 6; r++) rect(c, x + Math.floor(r / 2), y + 1 + r, Math.max(1, 6 - r - Math.floor(r / 2)), 1, col);
      }, 7);
    }
    const ly = plan.gallery ? T - 66 : Math.max(10, T - 32);
    const lanterns = ['#e8483a', '#f0b83a', '#e8783a', '#f8d868'];
    swag(12, W - 12, ly, 12, (x, y, t) => {
      rect(c, x, y, 2, 1, '#3a2a1a');
      const k = Math.round(t * 1000);
      if (Math.round(x) % 36 < 2) {
        const col = lanterns[k % lanterns.length]!;
        rect(g, x - 1, y + 1, 2, 2, '#3a2a1a');
        ellipse(g, x, y + 8, 5, 5, shade(col, -0.3)); ellipse(g, x, y + 8, 4, 4, col); rect(g, x - 1, y + 6, 2, 3, '#fff0b0');
        rect(g, x - 3, y + 3, 6, 1, '#3a2a1a'); rect(g, x - 3, y + 13, 6, 1, '#3a2a1a');
      }
    }, 2);
  }
}

function sillPot(c: Ctx, w: { x: number; y: number; ww: number; wh: number }, flowers: readonly string[]) {
  const x = w.x + w.ww - 7, y = w.y + w.wh;
  rect(c, x - 1, y - 4, 7, 5, INK); rect(c, x, y - 3, 5, 4, '#a85a3a'); rect(c, x, y - 3, 5, 1, '#c87a4a');
  for (let i = 0; i < 5; i++) { rect(c, x + i, y - 7 - (i % 2), 1, 4, '#3a6a2a'); px(c, x + i, y - 8 - (i % 2) * 2, flowers[i % flowers.length]!); }
}

// ---- pieces with their own depth ------------------------------------------------------------------
export interface Piece {
  key: string;
  /** Where the canvas's top-left stands (atlas px from the room's top-left). */
  x: number; y: number; w: number; h: number;
  z: number;
  draw(c: Ctx): void;
  /** Drawn after the tint (it shines). */
  lit?: (c: Ctx) => void;
}

const z = (y: number, d = 0) => Math.round(y * 10) + d;

/**
 * CI8: an armchair facing the fire, drawn round a 24 px sitter. The tall wing back stands behind them
 * (rising above their head) with the seat cushion they sit on; the rolled arm crosses at their waist and
 * the seat's front hides their legs, so their head and body show over the arm.
 */
function armchair(key: string, st: { x: number; y: number; flip?: boolean }, cloth: typeof CLOTH.red): Piece[] {
  // the piece's rows run 9 above the sitter's cell top: row 9 is the top of their head's cell
  const W = 30, H = 41, X = Math.round(st.x * CELL) - 4, Y = Math.round(st.y * CELL) - 9;
  const flip = !!st.flip;
  const m = (c: Ctx, draw: (c: Ctx) => void) => { if (flip) { c.save(); c.translate(W, 0); c.scale(-1, 1); draw(c); c.restore(); } else draw(c); };
  const back = (c: Ctx) => m(c, (c) => {
    // the wing back, tall and tufted, a little higher than the sitter's head, its wing curving forward
    rect(c, 0, 2, 10, 32, INK); rect(c, 1, 0, 8, 2, INK); rect(c, 9, 3, 5, 12, INK);
    rect(c, 1, 2, 8, 31, cloth.m); rect(c, 2, 1, 6, 1, cloth.h); rect(c, 1, 2, 2, 31, cloth.l); rect(c, 8, 4, 1, 29, cloth.d);
    rect(c, 9, 4, 4, 10, cloth.l); rect(c, 9, 4, 4, 1, cloth.h); rect(c, 12, 5, 1, 9, cloth.d);
    for (const [bx, by] of [[4, 7], [4, 13], [4, 19], [4, 25], [6, 10], [6, 16], [6, 22]] as const) { px(c, bx, by, cloth.d); px(c, bx - 1, by - 1, cloth.h); }
    // the far arm, seen past the sitter, and the seat cushion they sit on
    rect(c, 8, 22, 18, 4, INK); rect(c, 9, 23, 16, 2, cloth.d);
    rect(c, 7, 25, 21, 5, INK); rect(c, 8, 26, 19, 3, cloth.l); rect(c, 8, 26, 19, 1, cloth.h);
  });
  const front = (c: Ctx) => m(c, (c) => {
    // the near arm, rolled and plump, across the sitter's waist, its scroll at the front
    rect(c, 7, 24, 18, 6, INK); rect(c, 8, 25, 16, 4, cloth.m); rect(c, 8, 25, 16, 1, cloth.h); rect(c, 8, 26, 16, 1, cloth.l); rect(c, 8, 28, 16, 1, cloth.d);
    ellipse(c, 24.5, 27, 3.5, 3.5, INK); ellipse(c, 24.5, 27, 2.5, 2.5, cloth.l); px(c, 24, 26, cloth.h); rect(c, 24, 27, 2, 2, cloth.d);
    // the seat's front over the sitter's legs, piped, a gold fringe along it
    rect(c, 2, 29, 26, 7, INK); rect(c, 3, 30, 24, 5, cloth.m); rect(c, 3, 30, 24, 1, cloth.h); rect(c, 3, 34, 24, 1, cloth.d);
    for (let x = 3; x < 27; x += 2) px(c, x, 35, '#e0b050');
    // turned legs
    for (const lx of [4, 24]) { rect(c, lx - 1, 35, 4, 6, INK); rect(c, lx, 35, 2, 5, OAK.l); px(c, lx, 36, OAK.h); rect(c, lx - 1, 39, 4, 1, INK); }
  });
  return [{ key: `${key}:back`, x: X, y: Y, w: W, h: H, z: z(st.y, -1), draw: back }, { key: `${key}:front`, x: X, y: Y, w: W, h: H, z: z(st.y, 3), draw: front }];
}

/** A low three-legged stool in front of a sitter (their legs hidden behind its seat). */
function stoolFront(key: string, st: { x: number; y: number }, d = 2): Piece {
  const X = Math.round(st.x * CELL) + 3, Y = Math.round(st.y * CELL) + 15;
  return { key, x: X, y: Y, w: 18, h: 10, z: z(st.y, d), draw: (c) => {
    rect(c, 0, 0, 18, 4, INK); rect(c, 1, 1, 16, 2, OAK.l); rect(c, 1, 1, 16, 1, OAK.h);
    for (const lx of [2, 8, 14]) { rect(c, lx - 1, 3, 3, 7, INK); rect(c, lx, 3, 1, 6, OAK.m); }
  } };
}
function stoolBack(key: string, st: { x: number; y: number }): Piece { return { ...stoolFront(key, st, -1) }; }

/** CI4/CI5: the round dice table: a wooden rim round a green felt, a pedestal, a candle stub on the rim. */
function diceTable(plan: InnPlan): Piece[] {
  const d = plan.dice;
  const X = Math.round(d.x * CELL) - 4, Y = Math.round(d.y * CELL) - 2;
  const top: Piece = { key: 'dice:top', x: X, y: Y, w: 56, h: 36, z: z(d.y, 4), draw: (c) => {
    // the pedestal and its feet
    rect(c, 24, 18, 8, 13, INK); rect(c, 25, 18, 6, 12, OAK.m); rect(c, 25, 18, 2, 12, OAK.l);
    rect(c, 15, 30, 26, 4, INK); rect(c, 16, 31, 24, 2, OAK.m); rect(c, 16, 31, 24, 1, OAK.l);
    // the rim's thickness, then the top
    ellipse(c, 28, 18, 27, 8, INK); ellipse(c, 28, 17, 26, 7, OAK.d);
    ellipse(c, 28, 15, 27, 9, INK); ellipse(c, 28, 15, 26, 8, OAK.l);
    ellipse(c, 28, 14, 24, 6.5, OAK.h);
    ellipse(c, 28, 15, 23, 6.5, '#1f4a2a'); ellipse(c, 28, 15.5, 22, 5.5, '#2e6a3a');
    for (let i = 0; i < 26; i++) px(c, 8 + Math.floor(hashN(i, 7) * 40), 11 + Math.floor(hashN(i, 8) * 9), '#3a7a46');
    // brass studs round the rim
    for (let a = 0.2; a < Math.PI; a += 0.5) px(c, Math.round(28 + Math.cos(a) * 25), Math.round(15 + Math.sin(a) * 8), BRASS.m);
  } };
  const st = d.stools;
  return [top, ...[1, 2].flatMap((i) => (st[i] ? [stoolBack(`dice:stool${i}`, st[i]!)] : [])), ...[0, 3].flatMap((i) => (st[i] ? [stoolFront(`dice:stool${i}`, st[i]!)] : []))];
}

/** CI8: the little table by the armchairs: a round top on a turned leg, a book on it (its candle is a DOM flame). */
function sideTable(at: { x: number; y: number }): Piece {
  const X = Math.round(at.x * CELL) + 2, Y = Math.round(at.y * CELL) + 6;
  return { key: 'side', x: X, y: Y, w: 20, h: 20, z: z(at.y, 1), draw: (c) => {
    ellipse(c, 10, 4, 9, 3, INK); ellipse(c, 10, 4, 8, 2, OAK.l); rect(c, 3, 3, 14, 1, OAK.h);
    rect(c, 2, 5, 16, 2, INK); rect(c, 3, 5, 14, 1, OAK.d);
    rect(c, 8, 7, 4, 10, INK); rect(c, 9, 7, 2, 9, OAK.m); rect(c, 9, 10, 2, 1, OAK.h);
    rect(c, 4, 16, 12, 3, INK); rect(c, 5, 16, 10, 1, OAK.l);
    // a book lying on it
    rect(c, 11, 0, 7, 4, INK); rect(c, 12, 1, 5, 2, '#7a2a3a'); rect(c, 12, 2, 5, 1, '#efe4c8');
  } };
}

/** CI11: the guestbook's lectern: a slanted desk on a post, the book open on it, a quill in its pot. */
function lectern(at: { x: number; y: number }): Piece {
  const X = Math.round(at.x * CELL) + 1, Y = Math.round(at.y * CELL) - 9;
  return { key: 'lectern', x: X, y: Y, w: 22, h: 34, z: z(at.y, 0), draw: (c) => {
    // the post and foot
    rect(c, 9, 14, 5, 16, INK); rect(c, 10, 14, 3, 15, OAK.m); rect(c, 10, 14, 1, 15, OAK.l);
    rect(c, 3, 29, 17, 4, INK); rect(c, 4, 30, 15, 2, OAK.m); rect(c, 4, 30, 15, 1, OAK.l);
    // the slanted desk
    for (let k = 0; k < 6; k++) rect(c, 1, 8 + k, 21, 1, INK);
    for (let k = 0; k < 6; k++) rect(c, 2 + k, 7 - k, 19 - k * 2, 1, INK);
    rect(c, 2, 9, 19, 4, OAK.l); rect(c, 2, 9, 19, 1, OAK.h);
    // the open book: two pages, a ribbon
    for (let k = 0; k < 5; k++) { rect(c, 3 + k, 7 - k, 8 - k, 1, '#efe6cc'); rect(c, 12, 7 - k, 8 - k, 1, '#e4d8b8'); }
    rect(c, 11, 3, 1, 5, '#a89a7a');
    for (let k = 0; k < 3; k++) { rect(c, 5 + k, 6 - k, 4, 1, '#8a7a5a'); rect(c, 13, 6 - k, 4, 1, '#8a7a5a'); }
    rect(c, 15, 8, 1, 4, '#c8302a');
    // the inkpot and quill
    rect(c, 18, 4, 4, 4, INK); rect(c, 19, 5, 2, 2, '#2a2a3a');
    for (let k = 0; k < 6; k++) px(c, 20 + Math.floor(k / 3), -1 + k - 3 + 3, '#f4f0e4');
  } };
}

/** CI16: a bed upstairs: the headboard, the mattress and the pillow behind a sleeper; the patchwork quilt and the footboard over them. */
function bed(i: number, b: { x: number; y: number }, seed: number): Piece[] {
  const X = Math.round(b.x * CELL) - 2, Y = Math.round(b.y * CELL) - 8;
  const qs = [['#b8443a', '#e8c45a', '#4a7aa8', '#efe4c8', '#5a8a4a', '#c87a3a'], ['#7a4a8a', '#e8b8c8', '#4a8a7a', '#efe4c8', '#c8a050', '#3a5a8a'], ['#3a6a4a', '#e8d8a0', '#a83a3a', '#8ab0d0', '#efe4c8', '#d8a03a'], ['#8a5a3a', '#f0d8b0', '#4a6aa8', '#c84a4a', '#efe4c8', '#6a9a5a']][i % 4]!;
  const sleeperZ = Math.round((b.y - 0.15) * 10) + 1;
  const back: Piece = { key: `bed${i}:back`, x: X, y: Y, w: 54, h: 36, z: sleeperZ - 1, draw: (c) => {
    // the headboard, a heart cut in it
    rect(c, 0, 2, 8, 32, INK); rect(c, 1, 0, 6, 2, INK); rect(c, 1, 2, 6, 31, OAK.m); rect(c, 1, 2, 2, 31, OAK.l); rect(c, 2, 1, 4, 1, OAK.h);
    rect(c, 3, 7, 1, 1, OAK.d); rect(c, 5, 7, 1, 1, OAK.d); rect(c, 3, 8, 3, 1, OAK.d); px(c, 4, 9, OAK.d);
    // the mattress and the sheet
    rect(c, 6, 18, 44, 8, INK); rect(c, 7, 19, 42, 6, '#e8dcc0'); rect(c, 7, 19, 42, 1, '#f8f0e0');
    // the bed frame and its legs
    rect(c, 6, 25, 44, 5, INK); rect(c, 7, 26, 42, 3, OAK.m); rect(c, 7, 26, 42, 1, OAK.l);
    for (const lx of [7, 46]) { rect(c, lx - 1, 29, 4, 6, INK); rect(c, lx, 29, 2, 5, OAK.d); }
    // the pillow, plumped
    rect(c, 7, 13, 12, 7, INK); rect(c, 8, 12, 10, 1, INK); rect(c, 8, 13, 10, 6, '#f4f0e8'); rect(c, 8, 13, 10, 1, '#ffffff'); rect(c, 9, 17, 8, 1, '#d8d0c0');
  } };
  const front: Piece = { key: `bed${i}:front`, x: X, y: Y, w: 54, h: 36, z: sleeperZ + 3, draw: (c) => {
    // the quilt: patchwork squares, a folded-back sheet at its top, draped over the side
    rect(c, 17, 15, 31, 14, INK); rect(c, 18, 14, 29, 1, INK);
    for (let jy = 0; jy < 4; jy++) for (let ix = 0; ix < 8; ix++) rect(c, 18 + ix * 4 - (ix === 7 ? 1 : 0), 15 + jy * 3 + (jy ? 1 : 0), 4, jy ? 3 : 4, qs[Math.floor(hashN(seed, i, ix, jy) * qs.length)]!);
    rect(c, 18, 15, 4, 13, '#f4f0e8'); rect(c, 18, 15, 1, 13, '#ffffff'); rect(c, 21, 15, 1, 13, '#c8c0b0');
    for (let ix = 0; ix < 8; ix++) px(c, 20 + ix * 4, 28, '#2a1a10');
    // the footboard
    rect(c, 46, 9, 8, 26, INK); rect(c, 47, 7, 6, 2, INK); rect(c, 47, 9, 6, 25, OAK.m); rect(c, 47, 9, 2, 25, OAK.l); rect(c, 48, 8, 4, 1, OAK.h);
  } };
  return [back, front];
}

/** CI16: the wash stand: a little table with a basin and a jug, a towel on its rail. */
function washStand(at: { x: number; y: number }): Piece {
  const X = Math.round(at.x * CELL), Y = Math.round(at.y * CELL) - 6;
  return { key: 'wash', x: X, y: Y, w: 24, h: 30, z: z(at.y, 0), draw: (c) => {
    rect(c, 1, 12, 22, 4, INK); rect(c, 2, 13, 20, 2, OAK.l); rect(c, 2, 13, 20, 1, OAK.h);
    for (const lx of [3, 19]) { rect(c, lx - 1, 15, 4, 14, INK); rect(c, lx, 15, 2, 13, OAK.m); }
    rect(c, 2, 22, 20, 2, INK); rect(c, 3, 22, 18, 1, OAK.d);
    // the towel over the rail
    rect(c, 6, 15, 6, 8, INK); rect(c, 7, 15, 4, 7, '#e8e0d0'); rect(c, 7, 19, 4, 1, '#8ab0d0');
    // the basin and the jug
    ellipse(c, 9, 11, 7, 2, INK); ellipse(c, 9, 11, 6, 1.4, '#e8eef4'); rect(c, 4, 10, 10, 1, '#8ab0d0');
    rect(c, 15, 3, 6, 10, INK); rect(c, 16, 4, 4, 8, '#e8eef4'); rect(c, 16, 4, 1, 8, '#ffffff'); rect(c, 20, 6, 2, 4, INK); px(c, 14, 4, INK);
    rect(c, 16, 7, 4, 1, '#4a7aa8');
  } };
}

/** Every piece the inn (or the room upstairs) paints with its own depth. */
export function innPieces(room: Room, plan: InnPlan, upstairs: boolean, seed: number): Piece[] {
  if (upstairs) return [...plan.beds.flatMap((b, i) => bed(i, b, seed)), ...(plan.wash ? [washStand(plan.wash)] : [])];
  return [
    ...(plan.gallery ? [galleryRail(room, plan)] : []),
    ...(plan.armchairs[0] ? armchair('chair0', plan.armchairs[0], CLOTH.red) : []),
    ...(plan.armchairs[1] ? armchair('chair1', plan.armchairs[1], CLOTH.green) : []),
    ...(plan.side ? [sideTable(plan.side)] : []),
    lectern(plan.lectern),
    stoolFront('stool:teller', plan.storyteller),
    stoolFront('stool:bard', plan.bard),
    ...(plan.dice.w ? diceTable(plan) : []),
  ];
}

/** Paint a piece into its own canvas (tinted with the room's light). */
export function paintPiece(p: Piece, night: boolean): HTMLCanvasElement {
  const cv = document.createElement('canvas');
  cv.width = p.w; cv.height = p.h;
  const c = cv.getContext('2d')!;
  c.imageSmoothingEnabled = false;
  p.draw(c);
  tintCanvas(c, innTint(night));
  p.lit?.(c);
  return cv;
}
